import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { message } from "antd";
import { ResponseHandler } from "shared/types/axios";
import { translate } from "shared/languages";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import client from "shared/utils/axios-config";
import { DiscountCodeForm } from "shared/types";

// The server returns the raw string "duplicate_code" (createDiscountCode/
// updateDiscountCode, controllers/discount-code.ts) rather than a sentence —
// map it to something readable instead of showing that literal token, same
// as getApiErrorMessage does for every OTHER server message, just with a
// friendlier override for this one specific, expected case.
const onDiscountCodeError = (error: AxiosError<ResponseHandler>) => {
  if (error.response?.data?.message === "duplicate_code") {
    message.error(translate("discountCodes.duplicate_code_error"));
    return;
  }
  getApiErrorMessage(error);
};

const useCreateDiscountCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (form: DiscountCodeForm) => {
      return client.post(`/v1/discount-code`, form);
    },
    // Awaited: invalidateQueries only SCHEDULES a refetch, it doesn't wait
    // for it, so a bare (non-awaited) call let onSubmit's onClose() fire —
    // and the modal close — before the table had actually refetched. Reopen
    // the same row fast enough afterward and openEdit(record) still grabbed
    // the stale pre-refetch object; only a SECOND click, after the
    // background refetch had landed, showed the real update. Returning this
    // promise makes mutateAsync (and so onSubmit's `await update(...)`) not
    // resolve until the table's data is actually current.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["discount-codes"] });
      getApiSuccessMessage(
        `${translate("discountCodes.name")} ${translate("messages.success.create")}`,
      );
    },
    onError: onDiscountCodeError,
  });
};

const useUpdateDiscountCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...form }: DiscountCodeForm & { id: string }) => {
      return client.put(`/v1/discount-code/${id}`, form);
    },
    // See useCreateDiscountCode's comment — same await-before-resolving fix.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["discount-codes"] });
      getApiSuccessMessage(
        `${translate("discountCodes.name")} ${translate("messages.success.update")}`,
      );
    },
    onError: onDiscountCodeError,
  });
};

// Only ever called on a code the UI has already confirmed has zero
// redemptions (see DiscountCodesCard's Hapus button) — the server's own
// "has_redemptions" 409 is defense-in-depth, not the primary guard, so it
// gets its own friendly message rather than reusing onDiscountCodeError
// (which only knows about "duplicate_code").
const useDeleteDiscountCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return client.delete(`/v1/discount-code/${id}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["discount-codes"] });
      getApiSuccessMessage(
        `${translate("discountCodes.name")} ${translate("messages.success.delete")}`,
      );
    },
    onError: (error: AxiosError<ResponseHandler>) => {
      if (error.response?.data?.message === "has_redemptions") {
        message.error(translate("discountCodes.has_redemptions_error"));
        return;
      }
      getApiErrorMessage(error);
    },
  });
};

export { useCreateDiscountCode, useUpdateDiscountCode, useDeleteDiscountCode };
